import {
  type AssemblyCurrency,
  AssemblyCurrencyType,
} from '@assembly-web/services';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  Content as DropdownContent,
  Root as DropdownRoot,
  Trigger as DropdownTrigger,
} from '@radix-ui/react-dropdown-menu';
import dayjs from 'dayjs';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ProgressBar } from '../../../DesignSystem/Feedback/ProgressBar';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Tooltip } from '../../../DesignSystem/Feedback/Tooltip';
import { Button } from '../../../DesignSystem/Inputs/Button';
import { HorizontalRule } from '../../../DesignSystem/Layout/HorizontalRule';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { mapHexCodeToEmoticon } from '../PostCard/PostCardHeader';

const messages = defineMessages({
  earned: {
    defaultMessage: 'EARNED',
    id: '875mvK',
  },
  allowance: {
    defaultMessage: 'ALLOWANCE',
    id: 'fKzvcn',
  },
  allowanceTooltip: {
    defaultMessage:
      'This represents your monthly allowance and how much you’ve given this month. You have {totalAllowance} {currency} each month to give. Spread some joy!',
    id: 'Yb3tE6',
  },
  earnedBalanceTooltip: {
    defaultMessage: 'This represents your earned balance',
    id: '9d9pgm',
  },
  browseRewards: {
    defaultMessage: 'Browse Rewards',
    id: 'ug7T4L',
  },
  givenIn: {
    defaultMessage: 'Given in {month}',
    id: '3tHaZ/',
  },
  leftToGive: {
    defaultMessage: '{currency} {remainingAllowance} left to give in {month}',
    id: 'GeWKz5',
  },
  refreshesTo: {
    defaultMessage: 'Refreshes to {currency} {totalAllowance} on {month} 1',
    id: 'UyDvH7',
  },
  left: {
    defaultMessage: 'left',
    id: 'VFHHgv',
  },
  yourEarnedBalance: {
    defaultMessage: 'Your earned balance',
    id: 'nh6oB9',
  },
});

function RewardsDetailsTrigger({
  earnedBalance,
  remainingAllowance,
  currency,
  hideAllowanceDetails,
  hideEarnedPoints,
}: {
  earnedBalance: number;
  remainingAllowance: number;
  currency: AssemblyCurrency;
  hideAllowanceDetails: boolean;
  hideEarnedPoints: boolean;
}) {
  const { formatMessage } = useIntl();

  const currencyIcon =
    currency.type === AssemblyCurrencyType.Custom ? (
      <img
        alt={currency.name}
        className="mr-0.5 h-3 w-3"
        src={currency.value}
      />
    ) : (
      mapHexCodeToEmoticon(currency.value)
    );

  return (
    <div className="flex gap-2 space-x-2 divide-x divide-gray-3 rounded px-3 py-[3px] hover:divide-gray-10 hover:bg-upgrade-3 hover:text-upgrade-10 group-aria-expanded:divide-gray-10 group-aria-expanded:bg-upgrade-3">
      {!hideEarnedPoints && (
        <div className="flex flex-col">
          <TextStyle
            variant="xs-regular"
            className="text-gray-8 group-hover:text-upgrade-10 dark:text-gray-1"
          >
            {formatMessage(messages.earned)}
          </TextStyle>
          <TextStyle
            className="flex flex-row items-center justify-center"
            variant="sm-regular"
          >
            {currencyIcon} {earnedBalance}
          </TextStyle>
        </div>
      )}
      {!hideAllowanceDetails && (
        <div className="flex flex-col pl-4">
          <TextStyle
            variant="xs-regular"
            className="text-gray-8 group-hover:text-upgrade-10 dark:text-gray-1"
          >
            {formatMessage(messages.allowance)}
          </TextStyle>
          <TextStyle
            variant="sm-regular"
            className="flex flex-row items-center justify-center"
          >
            {currencyIcon} {remainingAllowance} {formatMessage(messages.left)}
          </TextStyle>
        </div>
      )}
    </div>
  );
}

export function RewardsDetailsContent({
  currency,
  earnedBalance,
  spentAllowance,
  totalAllowance,
  hideAllowanceDetails,
  hideRedeemButton,
  remainingAllowance,
  className,
  hideEarnedPoints,
  onBrowseRewardsClick,
}: {
  earnedBalance: number;
  spentAllowance: number;
  totalAllowance: number;
  currency: AssemblyCurrency;
  hideAllowanceDetails: boolean;
  hideRedeemButton: boolean;
  remainingAllowance: number;
  className?: string;
  hideEarnedPoints: boolean;
  onBrowseRewardsClick?: () => void;
}) {
  const percentage = parseInt(`${(spentAllowance / totalAllowance) * 100}`);
  const { formatMessage } = useIntl();

  const currencyIcon =
    currency.type === AssemblyCurrencyType.Custom ? (
      <img
        alt={currency.name}
        className="mr-0.5 h-3 w-3"
        src={currency.value}
      />
    ) : (
      mapHexCodeToEmoticon(currency.value)
    );

  let progress;

  if (percentage >= 100) {
    progress = (
      <>
        {`Refreshes to`} {currencyIcon}
        {` ${totalAllowance} on ${dayjs().add(1, 'month').format('MMMM')} 1`}
      </>
    );
  } else {
    progress = (
      <>
        {currencyIcon}
        {` ${remainingAllowance} left to give in ${dayjs().format('MMMM')}`}
      </>
    );
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 rounded bg-gray-1 p-4 shadow-2xl-down md:min-w-[300px]',
        className
      )}
    >
      {!hideAllowanceDetails && (
        <>
          <div className="flex items-center justify-between">
            <TextStyle variant="base-regular">
              {formatMessage(messages.givenIn, {
                month: dayjs().format('MMMM'),
              })}
            </TextStyle>
            <div className="flex items-center justify-between">
              <TextStyle
                className="flex flex-row items-center"
                variant="xs-regular"
              >
                {currencyIcon} {spentAllowance}
              </TextStyle>
              <TextStyle variant="xs-regular" className="text-gray-7">
                /{totalAllowance}
              </TextStyle>
              <Tooltip
                side="left"
                align="start"
                contentClassName="bg-gray-10 text-gray-1 leading-4 "
                tooltipText={formatMessage(messages.allowanceTooltip, {
                  totalAllowance,
                  currency: currency.pluralName,
                })}
              >
                <InformationCircleIcon className="ml-1 h-3 w-3 text-gray-7" />
              </Tooltip>
            </div>
          </div>

          <ProgressBar
            progress={percentage}
            aria-label={`Progress bar for showing remaining allowance`}
            indicatorBgColor={percentage >= 100 ? 'bg-error-6' : undefined}
          />
          <TextStyle
            variant="xs-regular"
            className="flex flex-row items-center text-gray-7"
          >
            {progress}
          </TextStyle>
        </>
      )}
      {!hideAllowanceDetails && !hideEarnedPoints && <HorizontalRule />}
      {!hideEarnedPoints && (
        <div className="flex justify-between">
          <TextStyle variant="base-regular">
            {formatMessage(messages.yourEarnedBalance)}
          </TextStyle>
          <div className="flex items-center">
            <TextStyle
              className="flex flex-row items-center"
              variant="xs-regular"
            >
              {currencyIcon} {earnedBalance}
            </TextStyle>
            <Tooltip
              side="left"
              contentClassName="bg-gray-10 text-gray-1"
              tooltipText={formatMessage(messages.earnedBalanceTooltip)}
            >
              <InformationCircleIcon className="ml-1 h-3 w-3 text-gray-7" />
            </Tooltip>
          </div>
        </div>
      )}
      {onBrowseRewardsClick && !hideRedeemButton && !hideEarnedPoints && (
        <Button
          onClick={() => {
            onBrowseRewardsClick();
          }}
          className="mt-2 h-8 w-full"
        >
          {formatMessage(messages.browseRewards)}
        </Button>
      )}
    </div>
  );
}

export function RewardsDetails({
  earnedBalance,
  spentAllowance,
  totalAllowance,
  currency,
  isRedeemable,
  isObserver,
  hideAllowanceDetails,
  remainingAllowance,
  hideEarnedPoints,
  onBrowseRewardsClick,
  onShowDetailsClick,
}: {
  earnedBalance: number;
  spentAllowance: number;
  totalAllowance: number;
  currency: AssemblyCurrency;
  isRedeemable: boolean;
  isObserver: boolean;
  hideAllowanceDetails: boolean;
  remainingAllowance: number;
  hideEarnedPoints: boolean;
  onBrowseRewardsClick?: () => void;
  onShowDetailsClick?: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const handleShowDetails = () => {
    if (!showDetails) {
      onShowDetailsClick?.();
    }
    setShowDetails(!showDetails);
  };

  if (isObserver) {
    return null;
  }

  return (
    <div className="relative inline-block text-left focus-visible:outline-none">
      <DropdownRoot onOpenChange={handleShowDetails} open={showDetails}>
        <DropdownTrigger asChild>
          <button className="group" onClick={() => setShowDetails(true)}>
            <RewardsDetailsTrigger
              earnedBalance={earnedBalance}
              remainingAllowance={remainingAllowance}
              currency={currency}
              hideAllowanceDetails={hideAllowanceDetails}
              hideEarnedPoints={hideEarnedPoints}
            />
          </button>
        </DropdownTrigger>
        <DropdownContent
          align="end"
          className="radix-side-top:animate-slide-up radix-side-bottom:animate-slide-down z-99"
          sideOffset={5}
        >
          <RewardsDetailsContent
            earnedBalance={earnedBalance}
            spentAllowance={spentAllowance}
            totalAllowance={totalAllowance}
            currency={currency}
            hideAllowanceDetails={hideAllowanceDetails}
            hideRedeemButton={!isRedeemable}
            remainingAllowance={remainingAllowance}
            hideEarnedPoints={hideEarnedPoints}
            onBrowseRewardsClick={() => {
              setShowDetails(false);
              onBrowseRewardsClick?.();
            }}
          />
        </DropdownContent>
      </DropdownRoot>
    </div>
  );
}
